import React, { useRef, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Checkbox,
  Typography,
  Divider,
  Alert,
  Table,
} from 'antd';
import {
  FileOutlined,
  FolderOpenOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { DirectoryUploadParams } from '@core/@models/ApiManualModel';
import { DirectoryTree } from '../shared/DirectoryTree';
import { CodeSnippetDisplay } from '../shared/CodeSnippetDisplay';

const { Text } = Typography;

const renderFieldCode = (v: string) => (
  <Typography.Text code>{v}</Typography.Text>
);
renderFieldCode.displayName = 'renderFieldCode';

const renderValueCell = (
  val: string | boolean | null,
  row: { key: string }
) => {
  if (row.key === 'isReplace') {
    return <Text code>{String(val)}</Text>;
  }
  if (val === null || val === '') {
    return (
      <Text type="secondary" style={{ fontStyle: 'italic' }}>
        —
      </Text>
    );
  }
  return (
    <Text code ellipsis style={{ maxWidth: 220 }}>
      {String(val)}
    </Text>
  );
};
renderValueCell.displayName = 'renderValueCell';

const MAX_PATH_LENGTH = 500;
const MAX_FILENAME_LENGTH = 255;
const MAX_FILE_DESCRIPTION_LENGTH = 255;

export const UploadTab: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedPath, setSelectedPath] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const [fileDescription, setFileDescription] = useState<string>('');
  const [isReplace, setIsReplace] = useState<boolean>(false);

  const handleFolderSelect = (_folderId: number | null, path: string) => {
    setSelectedPath(path);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setFileName(file.name);
    e.target.value = '';
  };

  const isFormComplete = () => {
    return selectedPath && fileName;
  };

  const getParams = (): DirectoryUploadParams => {
    return {
      destinationPath: selectedPath,
      fileName,
      fileDescription: fileDescription || undefined,
      isReplace,
    };
  };

  const pathLength = selectedPath?.length ?? 0;
  const filenameLength = fileName?.length ?? 0;
  const descriptionLength = fileDescription?.length ?? 0;
  const isPathTooLong = pathLength > MAX_PATH_LENGTH;
  const isFilenameTooLong = filenameLength > MAX_FILENAME_LENGTH;
  const isDescriptionTooLong = descriptionLength > MAX_FILE_DESCRIPTION_LENGTH;

  const pathParts = selectedPath ? selectedPath.split('/').filter(Boolean) : [];

  return (
    <div className="directory-upload-tab">
      <Card size="small" className="info-card">
        <Text type="secondary">
          <strong>POST /external/put/upload</strong> - Upload file to directory
          service.
          <br />
          Limits:
          <br />• Path ≤{MAX_PATH_LENGTH} characters
          <br />• Filename ≤{MAX_FILENAME_LENGTH} characters
          <br />• File description ≤{MAX_FILE_DESCRIPTION_LENGTH} characters
        </Text>
      </Card>

      <Card size="small" style={{ marginTop: 16 }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Request body uses <code>multipart/form-data</code>
        </Text>
        <Table
          size="small"
          pagination={false}
          showHeader
          className="multipart-form-table"
          dataSource={[
            {
              key: 'file',
              field: 'file',
              type: 'binary',
              required: 'Yes',
              description: 'Binary file to upload',
              value: fileName || null,
            },
            {
              key: 'destinationPath',
              field: 'destinationPath',
              type: 'string',
              required: 'Yes',
              description: 'Destination folder path',
              value: selectedPath || null,
            },
            {
              key: 'fileDescription',
              field: 'fileDescription',
              type: 'string',
              required: 'No',
              description: 'File description (uses filename when empty)',
              value: fileDescription.trim() || null,
            },
            {
              key: 'isReplace',
              field: 'isReplace',
              type: 'boolean',
              required: 'No (default: false)',
              description: 'Replace existing file; else auto-rename',
              value: isReplace,
            },
          ]}
          columns={[
            {
              title: 'Name',
              dataIndex: 'field',
              width: 140,
              render: renderFieldCode,
            },
            { title: 'Type', dataIndex: 'type', width: 90 },
            { title: 'Required', dataIndex: 'required', width: 120 },
            {
              title: 'Description',
              dataIndex: 'description',
              ellipsis: true,
              width: 200,
            },
            {
              title: 'Value',
              dataIndex: 'value',
              width: 200,
              render: renderValueCell,
            },
          ]}
        />
      </Card>

      <Divider />

      <Form layout="vertical">
        <Form.Item
          label="Upload to folder"
          required
          help="Select a folder from the tree below, Only folders you are allowed to upload to are listed."
        >
          <DirectoryTree
            onSelect={handleFolderSelect}
            selectedPath={selectedPath}
            permissionFilter="READ_WRITE"
          />
          {isPathTooLong && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
              message={`Path too long (${pathLength}/${MAX_PATH_LENGTH} chars). Select a shorter path.`}
            />
          )}
        </Form.Item>

        <Form.Item
          label="Filename"
          required
          help={
            filenameLength > 0
              ? `${filenameLength}/${MAX_FILENAME_LENGTH} characters`
              : 'Filename for the uploaded file. Choose file to auto-fill. Curl uses ./filename — run from the folder containing your file, or use full path.'
          }
        >
          <Input.Group compact style={{ display: 'flex' }}>
            <Input
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              placeholder="e.g. myfile.csv or click Choose file"
              maxLength={MAX_FILENAME_LENGTH}
              style={{ flex: 1 }}
            />
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <Button
              icon={<UploadOutlined />}
              onClick={() => fileInputRef.current?.click()}
            >
              Choose file
            </Button>
          </Input.Group>
          {isFilenameTooLong && (
            <Text type="danger" style={{ fontSize: 12, marginTop: 4 }}>
              Exceeds {MAX_FILENAME_LENGTH} characters
            </Text>
          )}
        </Form.Item>

        <Form.Item
          label="File Description"
          help={
            descriptionLength > 0
              ? `${descriptionLength}/${MAX_FILE_DESCRIPTION_LENGTH} characters`
              : 'Optional description for the file'
          }
        >
          <Input
            value={fileDescription}
            onChange={(e) => setFileDescription(e.target.value)}
            placeholder="Optional description"
            maxLength={MAX_FILE_DESCRIPTION_LENGTH}
          />
        </Form.Item>

        <Form.Item>
          <Checkbox
            checked={isReplace}
            onChange={(e) => setIsReplace(e.target.checked)}
          >
            Replace existing file (if unchecked, auto-rename with _1, _2, etc.)
          </Checkbox>
        </Form.Item>

        {isFormComplete() && !isPathTooLong && !isFilenameTooLong && (
          <Form.Item label="Upload Preview">
            <div
              style={{
                padding: 12,
                background: '#fafafa',
                borderRadius: 4,
                border: '1px solid #f0f0f0',
                fontFamily: 'monospace',
                fontSize: 12,
              }}
            >
              <div style={{ marginBottom: 4 }}>
                <UploadOutlined style={{ marginRight: 6, color: '#52c41a' }} />
                <Text strong>File will be uploaded to:</Text>
              </div>
              <div style={{ paddingLeft: 8 }}>
                {pathParts.map((part, i) => (
                  <div key={i} style={{ paddingLeft: i * 16 }}>
                    <FolderOpenOutlined
                      style={{
                        marginRight: 6,
                        color: '#faad14',
                        fontSize: 11,
                      }}
                    />
                    <span style={{ fontSize: 11, color: '#666' }}>{part}/</span>
                  </div>
                ))}
                <div
                  style={{
                    paddingLeft: pathParts.length * 16,
                  }}
                >
                  <FileOutlined
                    style={{
                      marginRight: 6,
                      color: '#1890ff',
                      fontSize: 11,
                    }}
                  />
                  <span style={{ fontSize: 11 }}>{fileName}</span>
                </div>
              </div>
              <div
                style={{
                  marginTop: 8,
                  fontSize: 11,
                  color: '#888',
                  fontFamily: 'inherit',
                }}
              >
                {selectedPath}/{fileName}
                {isReplace
                  ? ' (replace if exists)'
                  : ' (auto-rename if exists)'}
              </div>
            </div>
          </Form.Item>
        )}
      </Form>

      <Divider />

      <CodeSnippetDisplay
        apiType="directory-upload"
        params={getParams()}
        disabled={
          !isFormComplete() ||
          isPathTooLong ||
          isFilenameTooLong ||
          isDescriptionTooLong
        }
      />
    </div>
  );
};
